import { OverlayRef } from '@angular/cdk/overlay';
import { ESCAPE, TAB, hasModifierKey } from '@angular/cdk/keycodes';
import {
  FocusTrap,
  FocusTrapFactory,
  InteractivityChecker
} from '@angular/cdk/a11y';
import { Platform } from '@angular/cdk/platform';
import { Subject } from 'rxjs';
import { filter, take, takeUntil } from 'rxjs/operators';

/**
 * Give a raw CDK overlay the accessibility behavior a modal is required to
 * have. MatDialog does all of this for you; `overlay.create()` does none of it,
 * so the sidebars built directly on the CDK opened as unlabelled, role-less
 * panels that never took focus, never contained it, and could not be dismissed
 * from the keyboard.
 *
 * Applies:
 *  - role="dialog" + aria-modal="true" + aria-label      (WCAG 4.1.2)
 *  - a focus trap, with initial focus moved into the panel (WCAG 2.4.3)
 *  - Escape to dismiss                                   (WCAG 2.1.2)
 *  - focus restored to whatever was focused before opening
 *
 * Call on attach; call the returned teardown on detach.
 */
export interface AccessibleOverlayHandle {
  /** Run when the overlay detaches: releases the trap and restores focus. */
  release(): void;
}

export function makeOverlayAccessible(
  overlayRef: OverlayRef,
  focusTrapFactory: FocusTrapFactory,
  options: {
    ariaLabel: string;
    onEscape: () => void;
    /** Which controls Tab stops on. Defaults to the CDK's own checker. */
    checker?: Pick<InteractivityChecker, 'isFocusable' | 'isTabbable'>;
  }
): AccessibleOverlayHandle {
  const pane = overlayRef.overlayElement;
  // A detached or stubbed OverlayRef has no pane. Nothing to make accessible,
  // and nothing to tear down — return a no-op rather than throwing, so this
  // can never take down the caller.
  if (!pane) {
    return { release: () => undefined };
  }

  const previouslyFocused = document.activeElement as HTMLElement | null;

  pane.setAttribute('role', 'dialog');
  pane.setAttribute('aria-modal', 'true');
  pane.setAttribute('aria-label', options.ariaLabel);
  // Focusable so focus has somewhere to land without stealing it from a field.
  if (!pane.hasAttribute('tabindex')) pane.setAttribute('tabindex', '-1');

  let trap: FocusTrap | null = focusTrapFactory.create(pane);
  // Focus the panel itself rather than its first control: that announces the
  // dialog and keeps Tab inside it without jumping the scroll position.
  pane.focus({ preventScroll: true });

  const checker = options.checker ?? browserChecker();

  // Ends this opening's subscriptions. A reused OverlayRef (the Connect
  // sidebar reattaches the same one) otherwise stacked one Escape handler per
  // opening.
  const released = new Subject<void>();

  overlayRef
    .keydownEvents()
    .pipe(
      filter((event) => event.keyCode === ESCAPE && !hasModifierKey(event)),
      // A widget inside the panel (a menu, a combobox) that handled its own
      // Escape has said so; the panel stays open.
      filter((event) => !event.defaultPrevented),
      takeUntil(released)
    )
    .subscribe((event) => {
      event.preventDefault();
      options.onEscape();
    });

  /*
   * TAB WRAPS INSIDE THE PANEL (WCAG 2.4.3, audit 30 Sep).
   *
   * The CDK trap's anchors sit OUTSIDE the pane, as its siblings, and the
   * Connect sidebar's Tab walked past "Terms" into the page and the footer
   * anyway. So the wrap is also done here, on the overlay's own keydown
   * stream: Tab on the last focusable control goes to the first, Shift+Tab
   * on the first (or on the panel itself) goes to the last.
   */
  overlayRef
    .keydownEvents()
    .pipe(
      filter(
        (event) =>
          event.keyCode === TAB &&
          !event.altKey &&
          !event.ctrlKey &&
          !event.metaKey
      ),
      takeUntil(released)
    )
    .subscribe((event) => {
      const stops = tabbableIn(pane, checker);
      if (!stops.length) {
        event.preventDefault();
        pane.focus({ preventScroll: true });
        return;
      }
      const first = stops[0];
      const last = stops[stops.length - 1];
      const active = document.activeElement;
      if (
        event.shiftKey &&
        (active === first || active === pane || !pane.contains(active))
      ) {
        event.preventDefault();
        last.focus();
      } else if (
        !event.shiftKey &&
        (active === last || !pane.contains(active))
      ) {
        event.preventDefault();
        first.focus();
      }
    });

  // Backdrop click should behave the same as Escape.
  overlayRef
    .backdropClick()
    .pipe(take(1), takeUntil(released))
    .subscribe(() => options.onEscape());

  return {
    release(): void {
      released.next();
      released.complete();
      trap?.destroy();
      trap = null;
      // Only reclaim focus if it is still inside the panel we are closing;
      // otherwise the user has already moved on and we would yank them back.
      if (previouslyFocused && typeof previouslyFocused.focus === 'function') {
        const active = document.activeElement;
        if (!active || active === document.body || pane.contains(active)) {
          previouslyFocused.focus({ preventScroll: true });
        }
      }
    }
  };
}

/**
 * The controls Tab stops on inside the panel, in document order.
 *
 * The CDK's own checker (code review, 30 Sep): it knows a control inside a
 * collapsed or hidden section is not a stop, and that a disabled button with
 * tabindex 0 is not one either.
 */
function tabbableIn(
  root: HTMLElement,
  checker: Pick<InteractivityChecker, 'isFocusable' | 'isTabbable'>
): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>('*')).filter(
    (el) =>
      !el.closest('[inert]') &&
      checker.isFocusable(el) &&
      checker.isTabbable(el)
  );
}

/** A checker for the browser, when the caller has none to hand. */
function browserChecker(): InteractivityChecker {
  return new InteractivityChecker(new Platform('browser'));
}
